import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTenantId } from '../../utils/tenantIdentity';
import CompanyShatterLoader from '../../components/common/CompanyShatterLoader';
import businessSuiteService from '../services/businessSuiteService';
import { HD_ORCHIDS_BUSINESS_MODES } from '../config/moduleDefinitions';
import { workspaceAccessForPrincipal } from '../utils/access';

const SHARED_PREFIXES = [
  '/support',
  '/subscription-receipts',
  '/premium-addons',
];

const RestrictedWorkspace = () => (
  <div style={{minHeight:'55vh',display:'grid',placeItems:'center',padding:24}}>
    <div style={{maxWidth:520,textAlign:'center',padding:'28px 30px',border:'1px solid rgba(148,163,184,.25)',borderRadius:22,background:'#fff',boxShadow:'0 18px 50px rgba(15,23,42,.08)'}}>
      <div style={{fontSize:38}}>🔐</div>
      <h2 style={{margin:'10px 0 6px',color:'#0f172a'}}>Workspace access restricted</h2>
      <p style={{margin:0,color:'#64748b',fontWeight:650}}>Your assigned role does not include access to this business dashboard. Ask the company administrator to enable the required module permissions.</p>
    </div>
  </div>
);

export default function BusinessSuiteAccessRoute({ children }) {
  const { userData, roleData, permissions = [], isSuperAdmin = false } = useAuth();
  const location = useLocation();
  const tenantId = resolveTenantId(userData);
  const [state, setState] = React.useState({ loading: Boolean(tenantId), config: null });

  React.useEffect(() => {
    let active = true;
    if (!tenantId) {
      setState({ loading: false, config: null });
      return () => { active = false; };
    }
    businessSuiteService.getCompanyConfig(tenantId, true)
      .then((config) => active && setState({ loading: false, config }))
      .catch(() => active && setState({ loading: false, config: businessSuiteService.normalizeConfig({}) }));
    return () => { active = false; };
  }, [tenantId]);

  if (state.loading) return <CompanyShatterLoader label="Loading company workspace…" />;
  const config = state.config;
  if (!config) return children;

  const access = workspaceAccessForPrincipal({ userData, roleData, permissions, isSuperAdmin, companyMode: config?.mode });
  const path = location.pathname || '/';
  const isHdRoute = path === '/hd-orchids' || path.startsWith('/hd-orchids/');
  const isDashboard = path === '/company-dashboard';
  const shared = SHARED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

  if (isHdRoute) {
    if (!config.hdOrchids) return <Navigate to="/company-dashboard" replace />;
    if (!access.hdOrchids) {
      if (config.coldStorage && access.coldStorage) return <Navigate to="/company-dashboard" replace />;
      return <RestrictedWorkspace />;
    }
    return children;
  }

  if (shared) return children;

  if (isDashboard) {
    if (config.mode === HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS) return <Navigate to="/hd-orchids" replace />;
    if (!access.coldStorage && config.hdOrchids && access.hdOrchids) return <Navigate to="/hd-orchids" replace />;
    if (!access.coldStorage) return <RestrictedWorkspace />;
    return children;
  }

  if (config.mode === HD_ORCHIDS_BUSINESS_MODES.HD_ORCHIDS) return <Navigate to="/hd-orchids" replace />;
  if (!access.coldStorage) {
    if (config.hdOrchids && access.hdOrchids) return <Navigate to="/hd-orchids" replace />;
    return <RestrictedWorkspace />;
  }

  return children;
}
